class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Delegated click for popular-search chips in the empty state.
    this.addEventListener('click', this.onClickEmptyStateAction.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Empty input — render curated empty state instead of closing.
      this.renderEmptyState();
      this.open();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Pre-type discovery: show populares + bakers destacados.
      this.renderEmptyState();
      this.open();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      // Stay open with the empty-state visible — don't close on keystroke.
      this.renderEmptyState();
      this.open();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onClickEmptyStateAction(event) {
    const chip = event.target.closest('.predictive-search__chip');
    if (!chip) return;

    event.preventDefault();
    const term = chip.dataset.searchTerm || chip.textContent.trim();
    if (!term) return;

    this.input.value = term;
    this.input.focus();
    // Fire input event so SearchForm's debounced onChange picks it up.
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (currentButtonText.match(new RegExp(escapedTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll(
        'li:not([role="presentation"]), button.predictive-search__item, button.predictive-search__chip, a.predictive-search__bakery-card, a.predictive-search__pill'
      )
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"], a[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  /**
   * Render the curated pre-type empty state (Populares + Obradores destacados).
   * Reads JSON payloads from data-* attributes on the <predictive-search> host.
   * Both arrays may be empty — sections render only when their array has entries.
   */
  renderEmptyState() {
    let popular = [];
    let bakeries = [];

    try {
      const raw = this.dataset.popularSearches;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          popular = parsed.map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean);
        }
      }
    } catch (err) {
      popular = [];
    }

    try {
      const raw = this.dataset.featuredBakeries;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) bakeries = parsed.filter((b) => b && b.handle && b.url);
      }
    } catch (err) {
      bakeries = [];
    }

    const popularLabel = (this.dataset.labelPopular || 'Populares').toString();
    const bakeriesLabel = (this.dataset.labelFeaturedBakeries || 'Obradores destacados').toString();

    const escapeHtml = (str) =>
      String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    let html = '<div id="predictive-search-results" class="predictive-search__results predictive-search__empty-state" role="listbox">';

    // Prefer the server-rendered pills template (mascot pills linking to collections).
    // Fall back to the legacy text chips built from data-popular-searches.
    const pillsTemplate = this.querySelector('template[data-predictive-search-pills-template]');
    if (pillsTemplate && pillsTemplate.innerHTML.trim().length > 0) {
      html += pillsTemplate.innerHTML;
    } else if (popular.length > 0) {
      html += `<section class="predictive-search__empty-section" aria-labelledby="predictive-search-popular-heading">`;
      html += `<h2 id="predictive-search-popular-heading" class="predictive-search__empty-heading caption-with-letter-spacing">${escapeHtml(popularLabel)}</h2>`;
      html += `<ul class="predictive-search__chip-list" role="group" aria-labelledby="predictive-search-popular-heading">`;
      popular.forEach((term, idx) => {
        const id = `predictive-search-option-popular-${idx + 1}`;
        html += `<li class="predictive-search__chip-list-item" role="presentation"><button type="button" id="${id}" class="predictive-search__chip predictive-search__item" data-search-term="${escapeHtml(term)}" role="option" aria-selected="false" tabindex="-1">${escapeHtml(term)}</button></li>`;
      });
      html += `</ul></section>`;
    }

    if (bakeries.length > 0) {
      html += `<section class="predictive-search__empty-section" aria-labelledby="predictive-search-bakeries-heading">`;
      html += `<h2 id="predictive-search-bakeries-heading" class="predictive-search__empty-heading caption-with-letter-spacing">${escapeHtml(bakeriesLabel)}</h2>`;
      html += `<ul class="predictive-search__bakery-list" role="group" aria-labelledby="predictive-search-bakeries-heading">`;
      bakeries.forEach((b, idx) => {
        const id = `predictive-search-option-bakery-${idx + 1}`;
        const img = b.image
          ? `<img class="predictive-search__bakery-card-thumb" src="${escapeHtml(b.image)}" alt="${escapeHtml(b.title || '')}" width="56" height="56" loading="lazy">`
          : `<span class="predictive-search__bakery-card-thumb" aria-hidden="true"></span>`;
        const tagline = b.tagline
          ? `<p class="predictive-search__bakery-card-tagline">${escapeHtml(b.tagline)}</p>`
          : '';
        html += `<li class="predictive-search__bakery-list-item" role="presentation"><a id="${id}" class="predictive-search__bakery-card" href="${escapeHtml(b.url)}" role="option" aria-selected="false" tabindex="-1">${img}<div class="predictive-search__bakery-card-body"><p class="predictive-search__bakery-card-title">${escapeHtml(b.title || '')}</p>${tagline}</div></a></li>`;
      });
      html += `</ul></section>`;
    }

    html += '</div>';

    this.predictiveSearchResults.innerHTML = html;
    // Mark that we have results in the DOM (empty-state counts as renderable content).
    this.setAttribute('results', 'true');
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,query&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
